import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useClientDataContext } from '../contexts/ClientDataContext';

const DELAY_MS = parseInt(import.meta.env.VITE_FEEDBACK_NUDGE_DELAY_MS || '30000', 10);
const POSTPONE_MS = parseInt(import.meta.env.VITE_FEEDBACK_NUDGE_POSTPONE_MS || '86400000', 10);

const NUDGE_VERSION = import.meta.env.VITE_FEEDBACK_NUDGE_VERSION || '1';
const LS_DISMISSED = 'feedbackNudgeDismissed';
const LS_POSTPONED = 'feedbackNudgePostponedUntil';

interface Props {
  isPreviewMode: boolean;
}

export function FeedbackNudge({ isPreviewMode }: Props) {
  const { clientData } = useClientDataContext();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onFeedbackOpened = () => handleDismiss();
    window.addEventListener('feedbackOpened', onFeedbackOpened);
    return () => window.removeEventListener('feedbackOpened', onFeedbackOpened);
  }, []);

  useEffect(() => {
    if (!clientData?.feedbackNudgeEnabled || !isPreviewMode) return;
    if (localStorage.getItem(LS_DISMISSED) === NUDGE_VERSION) return;

    const postponedUntil = parseInt(localStorage.getItem(LS_POSTPONED) || '0', 10);
    const now = Date.now();
    const waitMs = postponedUntil > now ? postponedUntil - now + DELAY_MS : DELAY_MS;

    const timer = setTimeout(() => setVisible(true), waitMs);
    return () => clearTimeout(timer);
  }, [clientData?.feedbackNudgeEnabled, isPreviewMode]);

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem(LS_DISMISSED, NUDGE_VERSION);
  };

  const handlePostpone = () => {
    setVisible(false);
    localStorage.setItem(LS_POSTPONED, String(Date.now() + POSTPONE_MS));
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '80px',
        right: '24px',
        zIndex: 200,
        transform: visible ? 'translateY(0)' : 'translateY(12px)',
        opacity: visible ? 1 : 0,
        transition: 'transform 380ms cubic-bezier(0.4, 0, 0.2, 1), opacity 320ms ease',
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      <div className="relative bg-white rounded-xl shadow-lg border border-gray-100 px-3.5 py-3 w-64">
        {/* Dismiss */}
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-gray-300 hover:text-gray-500 transition-colors focus:outline-none"
          aria-label="Dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>

        <p className="text-xs font-semibold text-gray-800 pr-5 leading-snug mb-1">
          Share your experience
        </p>
        <p className="text-xs text-gray-500 leading-relaxed mb-3">
          Your input helps us improve. Click <span className="font-medium text-gray-700">Share Feedback</span> below — it only takes a moment.
        </p>
        <button
          onClick={handlePostpone}
          className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
        >
          Remind me later
        </button>

        {/* Arrow pointing down toward Share Feedback button */}
        <div
          className="absolute -bottom-2 w-4 h-2 overflow-hidden"
          style={{ right: '110px' }}
          aria-hidden="true"
        >
          <div
            className="w-3 h-3 bg-white border-r border-b border-gray-100 rotate-45 translate-y-[-6px] translate-x-[2px]"
          />
        </div>
      </div>
    </div>
  );
}
